import './perks-badge.component.scss';

import { h } from 'preact';

import { IconCheckBadge } from '../../../common/icons/icon-check-badge';
import { useFeatureFlag } from '../../../feature-flags/hooks/use-feature-flag.hook';
import { FeatureFlagName } from '../../../feature-flags/models/feature-flag-names.enum';
import { useTranslation } from '../../../i18n';
import type { User } from '../../model/user.interface';

export type BadgeKind = 'artist' | 'team';

/**
 * Badge size on the avatars in and around posts and comments — the comment header and the post
 * byline.
 */
export const AVATAR_BADGE_SIZE_PX = 15;
/**
 * Badge size on the compact avatars: the one beside a comment input and the feed's top-comment
 * strip. Those avatars are 30–36px, where the 15px badge covers a quarter of the face.
 */
export const COMPACT_AVATAR_BADGE_SIZE_PX = 10;
/** Badge size on the large profile avatars: the posts sidebar and My Account. */
export const PROFILE_BADGE_SIZE_PX = 20;
/**
 * Size for the inline placement beside a name, where there is no avatar to sit on. One number for
 * every such spot: the badge lands in different font contexts, so an em size would come out a
 * different number of pixels in each.
 */
const INLINE_SIZE_PX = 14;

/**
 * Fixed colours, not theme-driven: the check has to read as "verified" the same way on every store,
 * whatever the artist picked for their buttons. They go onto the glyph as an inline fill rather than
 * as a `color` on the span: the account-settings text colour is applied to every span in a post and
 * card with `!important` (perks.component.scss), which beat the stylesheet colour and painted the
 * seal in the text colour -- black on the first store the feature shipped to.
 */
const BADGE_COLORS: Record<BadgeKind, string> = {
  artist: '#0095f6',
  team: '#4caf50',
};

/** Artist wins over Team: account staff are moderators too, and the artist is the more specific fact. */
export const badgeKindForUser = (user: User | null | undefined): BadgeKind | null => {
  if (!user) return null;
  if (user.officialAuthor) return 'artist';
  if (user.moderator) return 'team';
  return null;
};

interface Props {
  kind: BadgeKind | null | undefined;
  /**
   * Badge size in px. Passing it sits the badge on the top-right edge of the avatar it is rendered
   * inside — that element is the positioning context — and leaving it out gives the inline
   * placement beside a name.
   *
   * A pinned number rather than a share of the avatar: a share has to survive whatever a store
   * theme does to the avatar's own box, and every surface here has one sensible badge size anyway.
   * Use AVATAR_BADGE_SIZE_PX, COMPACT_AVATAR_BADGE_SIZE_PX or PROFILE_BADGE_SIZE_PX rather than a
   * literal.
   */
  size?: number;
  /** extra classes on the badge itself, e.g. a responsive visibility class — nothing renders when there is no badge */
  class?: string;
}

/**
 * Check-badge glyph on an avatar (or beside a name where there is no avatar): blue for the artist,
 * green for the team. The label lives in title + aria-label so hover and screen readers both get
 * "Verified" / "Team".
 * Rides the community-official-authors flag: the whole author-identity feature is turned on per store.
 */
export const PerksBadge = ({ kind, size, class: className }: Props) => {
  const { t } = useTranslation();
  const enabled = useFeatureFlag(FeatureFlagName.COMMUNITY_OFFICIAL_AUTHORS);
  if (!kind || !enabled) return null;
  const label = t(kind === 'artist' ? 'perks.badge.artist' : 'perks.badge.team');
  const classes = ['single-badge', `single-badge-${kind}`];
  if (size) classes.push('single-badge-on-avatar');
  if (className) classes.push(className);
  // Both the box and the glyph carry the size inline. A store theme's own span and svg rules
  // outrank our stylesheet -- they were holding the glyph at its intrinsic size while the box grew
  // around it -- and an inline style is what beats them.
  const px = size ?? INLINE_SIZE_PX;
  return (
    <span
      class={classes.join(' ')}
      style={{ width: `${px}px`, height: `${px}px`, padding: 0 }}
      role="img"
      title={label}
      aria-label={label}
    >
      <IconCheckBadge width={px} height={px} fill={BADGE_COLORS[kind]} />
    </span>
  );
};
